import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  "Government",
  "Corporate Public Policy / Government Affairs",
  "Lobbying / Public Affairs",
  "Journalism / Media",
  "Research / Academia",
  "Campaign / Political",
  "Nonprofit / Advocacy",
  "General / Other",
];

const inputCls =
  "w-full rounded-lg border border-white/15 bg-white/[0.03] px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-orange-500/60 transition-colors";

export const EarlyAccessForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role_title: "", intended_use: "", audience: "General / Other",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/waitlist`, form);
      setDone(true);
      toast.success(r.data?.message || "Request received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed, please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="waitlist-success">
        <CheckCircle2 className="w-7 h-7 text-emerald-400 mx-auto mb-3" />
        <h3 className="text-white font-bold text-lg">Request received</h3>
        <p className="text-white/55 text-sm mt-1.5">
          We review every application personally and will reach out with access details.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-white/10 bg-white/[0.02] p-6 sm:p-8" data-testid="waitlist-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <input required minLength={2} placeholder="Full name *" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={inputCls} data-testid="waitlist-name-input" />
        <input required type="email" placeholder="Professional email *" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={inputCls} data-testid="waitlist-email-input" />
        <input placeholder="Organization" value={form.organization}
          onChange={(e) => setForm({ ...form, organization: e.target.value })}
          className={inputCls} data-testid="waitlist-org-input" />
        <input placeholder="Role / title" value={form.role_title}
          onChange={(e) => setForm({ ...form, role_title: e.target.value })}
          className={inputCls} data-testid="waitlist-role-input" />
      </div>
      <select value={form.audience}
        onChange={(e) => setForm({ ...form, audience: e.target.value })}
        className={`${inputCls} mt-4`} data-testid="waitlist-audience-select">
        {AUDIENCES.map((a) => (
          <option key={a} value={a} className="bg-[#12121a]">{a}</option>
        ))}
      </select>
      <textarea rows={3} placeholder="How do you plan to use WHIP?" value={form.intended_use}
        onChange={(e) => setForm({ ...form, intended_use: e.target.value })}
        className={`${inputCls} mt-4 resize-none`} data-testid="waitlist-use-input" />
      <button type="submit" disabled={busy}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-50 px-6 py-2.5 text-sm font-bold text-white transition-colors"
        data-testid="waitlist-submit-button">
        <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Request Early Access"}
      </button>
      <p className="text-[11px] text-white/35 mt-3">
        Access is reviewed individually. We'll only use this information to evaluate your request.
      </p>
    </form>
  );
};
